<script setup>
const { downloadProgress, cancelDownload, formatSize } = useHLSCache()

const activeDownloads = computed(() =>
  Object.values(downloadProgress.value).filter(d =>
    ['downloading', 'fetching_playlist', 'done', 'error', 'cancelled'].includes(d.status)
  )
)

const isActive = (d) => d.status === 'downloading' || d.status === 'fetching_playlist'

const getPercent = (d) => {
  if (!d.total || d.total === 0) return 0
  return Math.round((d.done / d.total) * 100)
}

// Розрахунок ETA на основі швидкості
const getETA = (d) => {
  if (!isActive(d) || !d.startTime || !d.done || !d.total) return null
  const elapsed = (Date.now() - d.startTime) / 1000 // секунди
  if (elapsed < 2) return null // чекаємо хоча б 2 сек для точності
  const rate = d.done / elapsed // сегментів/сек
  if (rate <= 0) return null
  const remaining = d.total - d.done
  const etaSec = Math.round(remaining / rate)
  if (etaSec <= 0) return null
  if (etaSec < 60) return `~${etaSec}с`
  const m = Math.floor(etaSec / 60)
  const s = etaSec % 60
  return s > 0 ? `~${m}хв ${s}с` : `~${m}хв`
}

// Швидкість завантаження
const getSpeed = (d) => {
  if (!isActive(d) || !d.startTime || !d.bytesDone) return null
  const elapsed = (Date.now() - d.startTime) / 1000
  if (elapsed < 1) return null
  const bps = d.bytesDone / elapsed
  return formatSize(bps) + '/с'
}

const getLabel = (d) => {
  if (d.status === 'fetching_playlist') return 'Отримання плейлиста...'
  if (d.status === 'done') return 'Завантажено!'
  if (d.status === 'error') return 'Помилка завантаження'
  if (d.status === 'cancelled') return 'Скасовано'
  const eta = getETA(d)
  const speed = getSpeed(d)
  let label = `${d.done} / ${d.total} сегм.`
  if (speed) label += `  •  ${speed}`
  if (eta) label += `  •  ${eta}`
  return label
}

// Прибираємо завершені/помилкові через 4 сек
watch(downloadProgress, (val) => {
  Object.entries(val).forEach(([key, d]) => {
    if (d.status === 'done' || d.status === 'error') {
      setTimeout(() => {
        const prog = { ...downloadProgress.value }
        if (prog[key]?.status === d.status) {
          delete prog[key]
          downloadProgress.value = prog
        }
      }, 4000)
    }
  })
}, { deep: true })
</script>

<template>
  <Teleport to="body">
    <div class="bottom-[10px] left-[10px] z-[9999] fixed flex flex-col gap-2 pointer-events-none">
      <TransitionGroup name="toast">
        <div
          v-for="d in activeDownloads"
          :key="`${d.animeId}-${d.episodeId}`"
          class="flex items-stretch bg-neutral shadow-xl rounded-xl w-[calc(100vw-20px)] lg:w-96 overflow-hidden pointer-events-auto"
        >
          <!-- Обкладинка -->
          <div class="bg-base-300 w-16 shrink-0">
            <img
              v-if="d.posterUrl"
              :src="d.posterUrl"
              :alt="d.animeTitle"
              class="w-full h-full object-cover"
            />
            <div v-else class="flex justify-center items-center opacity-30 w-full h-full">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24">
                <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m0 16H5V5h14z"/>
              </svg>
            </div>
          </div>

          <!-- Контент -->
          <div class="flex flex-col flex-1 gap-1.5 px-4 py-3 min-w-0">
            <!-- Заголовок + кнопка скасування -->
            <div class="flex justify-between items-start gap-2">
              <div class="flex-1 min-w-0">
                <div class="font-semibold text-base truncate leading-tight">
                  {{ d.animeTitle || d.animeId }}
                </div>
                <div class="text-sm text-base-content text-opacity-60 truncate">
                  {{ d.episodeTitle || `Серія ${d.episodeId}` }}
                </div>
              </div>
              <div class="flex items-center gap-2 ml-1 shrink-0">
                <span
                  class="font-bold tabular-nums text-sm"
                  :class="{
                    'text-success': d.status === 'done',
                    'text-error': d.status === 'error',
                    'text-warning': d.status === 'cancelled',
                    'text-primary': isActive(d)
                  }"
                >
                  <template v-if="d.status === 'done'">✓</template>
                  <template v-else-if="d.status === 'error'">✗</template>
                  <template v-else-if="d.status === 'cancelled'">—</template>
                  <template v-else>{{ getPercent(d) }}%</template>
                </span>
                <button
                  v-if="isActive(d)"
                  @click="cancelDownload(d.animeId, d.episodeId)"
                  class="flex justify-center items-center bg-base-content hover:bg-error bg-opacity-10 hover:bg-opacity-80 rounded-full w-6 h-6 transition-colors"
                  title="Скасувати"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Прогрес бар -->
            <progress
              class="w-full h-1.5 progress"
              :class="{
                'progress-success': d.status === 'done',
                'progress-error': d.status === 'error',
                'progress-warning': d.status === 'cancelled',
                'progress-primary': isActive(d)
              }"
              :value="d.done"
              :max="d.total || 1"
            ></progress>

            <!-- Підпис з ETA -->
            <div
              class="tabular-nums text-sm leading-tight"
              :class="{
                'text-success': d.status === 'done',
                'text-error': d.status === 'error',
                'text-warning': d.status === 'cancelled',
                'text-base-content text-opacity-50': isActive(d)
              }"
            >
              {{ getLabel(d) }}
            </div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}
.toast-enter-from {
  opacity: 0;
  transform: translateY(16px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}
</style>
